// 디자인 토큰 — 팔레트 / 타이포 / 모양 값을 한 곳에서 관리한다.
// MUI 테마(appTheme.ts)와 CSS 변수(tokens.css)가 같은 값을 참조하도록 하는 기준점.

import type { PaletteOptions, TypographyVariantsOptions } from "@mui/material/styles";

// 외부 폰트 / CDN 의존 없이 시스템 폰트 스택만 사용한다.
export const fontFamily = [
  "-apple-system",
  "BlinkMacSystemFont",
  '"Apple SD Gothic Neo"',
  '"Malgun Gothic"',
  '"Segoe UI"',
  "Roboto",
  "sans-serif",
].join(",");

// 라이트 팔레트 — cool neutral 배경 + 절제된 단일 프라이머리.
// success/warning/error/info는 light·dark·contrastText까지 명시한다.
// (createTheme 2번째 인자로는 augmentColor가 재실행되지 않아 .main만 바꾸면
//  나머지 톤이 MUI 구 기본값으로 남기 때문 — appTheme.ts에서 1번째 인자로 넘기지만
//  값을 명시해 두는 편이 StatusChip 등 소비처에서 예측 가능하다.)
export const lightPalette: PaletteOptions = {
  mode: "light",
  primary: { main: "#2B59C3", light: "#5C82D6", dark: "#1E3E8A", contrastText: "#FFFFFF" },
  secondary: { main: "#5B6474", light: "#828B9C", dark: "#3C4453", contrastText: "#FFFFFF" },
  background: { default: "#F6F7F9", paper: "#FFFFFF" },
  text: { primary: "#1B2333", secondary: "#5A6478", disabled: "#9AA1AE" },
  divider: "#E4E7EC",
  success: { main: "#2E7D5B", light: "#4CAF7D", dark: "#1F5D42", contrastText: "#FFFFFF" },
  // warning.main(#B7791F) on #fff는 AA 미달 → filled용 contrastText를 어두운색으로
  warning: { main: "#B7791F", light: "#D9A441", dark: "#8A5A12", contrastText: "#1B2333" },
  error: { main: "#D64545", light: "#E27373", dark: "#A32E2E", contrastText: "#FFFFFF" },
  info: { main: "#2B59C3", light: "#5C82D6", dark: "#1E3E8A", contrastText: "#FFFFFF" },
  // grey 50~900 전체 램프(다크 모드에서 300 이상이 near-white로 튀지 않도록)
  grey: {
    50: "#F1F3F7", 100: "#E9ECF2", 200: "#DDE1E9", 300: "#CBD2DE", 400: "#9AA4B4",
    500: "#727C8D", 600: "#55606F", 700: "#3E4757", 800: "#2A313D", 900: "#1A1F27",
  },
  action: { hover: "rgba(43,89,195,0.06)", selected: "rgba(43,89,195,0.10)" },
};

// 다크 팔레트 — 라이트와 동일한 역할 구조를 유지한 채 값만 교체
export const darkPalette: PaletteOptions = {
  mode: "dark",
  primary: { main: "#6C9CFF", light: "#8FB4FF", dark: "#3E6BD6", contrastText: "#0E1017" },
  secondary: { main: "#9AA4B8", light: "#B7BECC", dark: "#6B7382", contrastText: "#0E1017" },
  background: { default: "#14171F", paper: "#1B1F2A" },
  text: { primary: "#E6E9F0", secondary: "#9AA4B8", disabled: "#616B7D" },
  divider: "#2A3040",
  success: { main: "#4FB07E", light: "#74C79A", dark: "#37845C", contrastText: "#0E1017" },
  warning: { main: "#D9A441", light: "#E6BE73", dark: "#A87C25", contrastText: "#0E1017" },
  error: { main: "#E5776F", light: "#EF9A94", dark: "#C4544C", contrastText: "#0E1017" },
  info: { main: "#6C9CFF", light: "#8FB4FF", dark: "#3E6BD6", contrastText: "#0E1017" },
  grey: {
    50: "#232733", 100: "#2A2F3C", 200: "#333A49", 300: "#3E4657", 400: "#55606F",
    500: "#78889C", 600: "#9AA4B8", 700: "#B7BECC", 800: "#D5DAE2", 900: "#ECEEF2",
  },
  action: { hover: "rgba(108,156,255,0.10)", selected: "rgba(108,156,255,0.16)" },
};

// 타이포그래피 스케일 — 저시력 사용자 접근성을 위해 표준 크기로 상향(본문 16 / 보조 15).
// list/show 화면 가독성 개선 요청(2026-09) — 전 변형에 +1~2px, 위계(비율)는 유지.
export const typography: TypographyVariantsOptions = {
  fontFamily,
  h4: { fontSize: "1.625rem", fontWeight: 700, lineHeight: 1.3, letterSpacing: "-0.01em" },
  h5: { fontSize: "1.375rem", fontWeight: 700, lineHeight: 1.35, letterSpacing: "-0.01em" },
  h6: { fontSize: "1.1875rem", fontWeight: 700, lineHeight: 1.4 },
  subtitle1: { fontSize: "1.0625rem", fontWeight: 600, lineHeight: 1.5 },
  subtitle2: { fontSize: "0.9375rem", fontWeight: 600, lineHeight: 1.5 },
  body1: { fontSize: "1rem", fontWeight: 400, lineHeight: 1.55 },
  body2: { fontSize: "0.9375rem", fontWeight: 400, lineHeight: 1.5 },
  button: { fontSize: "0.9375rem", fontWeight: 600, lineHeight: 1.2, textTransform: "none" },
  caption: { fontSize: "0.8125rem", fontWeight: 400, lineHeight: 1.4 },
};

// 인풋 / 버튼 8px, 카드 12px, Chip 6px
export const shape = { borderRadius: 8 };
